import React, { useState } from "react";
import PropTypes from "prop-types";
import "./style.scss";
const DATA_ID = "data-id";
const SPACE_BAR = " ";
const SPLIT_CHARACTER = "";
const IMG_TAG = "img";
const RIGHT_ARROW = 39;
const LEFT_ARROW = 37;

let onChangeWait = null;
/**
 * OverlaySlider:
 *  - Component display a string sequence
 *  - A set of adjecent characters in sequnce will be highlighted.
 *  - User can drag the higlighting left and right to select sequence of characters
 */
export function OverlaySlider(props) {
  const indexSize = props.highlight[1];
  const [startIndex, setStartIndex] = useState(props.highlight[0]);
  const [characterList] = useState(props.sequence.split(SPLIT_CHARACTER));

  const disableEvent = (event) => {
    const img = document.createElement(IMG_TAG);
    img.src = "";
    event.dataTransfer.setDragImage(img, 0, 0);
  };

  const dragOver = (event) => {
    event.preventDefault();
    const dataId = parseInt(event.target.getAttribute(DATA_ID));
    const startIndexLimit = props.sequence.length - indexSize;
    if (dataId > startIndexLimit) {
      setStartIndex(startIndexLimit);
    } else {
      setStartIndex(dataId);
    }
    setOnChange();
  };

  const setOnChange = () => {
    if (onChangeWait) {
      clearTimeout(onChangeWait);
    }
    onChangeWait = setTimeout(() => {
      props.onChange(getActiveSequence());
    }, props.onChangeTimeout);
  };

  const getActiveSequence = () => {
    const end = startIndex + indexSize;
    const text = props.sequence.substring(startIndex, end);
    return {
      text,
      start: startIndex,
      end,
    };
  };

  const getHighlightClass = (tagIndex) => {
    let className = "ov-slider__sequence--selected " + props.highlightClass;
    if (tagIndex === indexSize) {
      className += " first";
    }
    if (tagIndex === 1) {
      className += " last";
    }
    return className;
  };

  const onKeyDown = (event) => {
    if (!props.disable) {
      if (event.keyCode === LEFT_ARROW) {
        if (startIndex > 0) {
          setStartIndex((s) => (s -= 1));
        }
      }
      if (event.keyCode === RIGHT_ARROW) {
        const end = startIndex + indexSize;
        if (end < props.sequence.length) {
          setStartIndex((s) => (s += 1));
        }
      }
      setOnChange();
    }
  };

  const listSequence = () => {
    let toHighlight = 0;
    const charTags = [];
    for (const ch in characterList) {
      const commonProps = {
        className: "ov-slider__sequence--character",
        key: ch,
        draggable: false,
        onDragStart: disableEvent,
        onDragOver: dragOver,
        dataId: ch,
        character: characterList[ch],
      };
      if (ch == startIndex) {
        toHighlight = indexSize;
      }
      if (toHighlight > 0) {
        commonProps.draggable = !props.disable;
        commonProps.className = getHighlightClass(toHighlight);
        toHighlight -= 1;
      }
      charTags.push(<CharacterTag {...commonProps} />);
    }
    return charTags;
  };

  const sliderLabel = () => {
    if (props.label) {
      return <div className="ov-slider__label">{props.label}</div>;
    } else return null;
  };

  return (
    <div className="overlay-slider">
      <div className="ov-slider">
        {sliderLabel()}
        <div
          className="ov-slider__sequence"
          role="button"
          tabIndex="0"
          onKeyDown={onKeyDown}
        >
          {listSequence()}
        </div>
      </div>
    </div>
  );
}

OverlaySlider.propTypes = {
  /**
   * The string sequence to be displayed.
   */
  sequence: PropTypes.string.isRequired,
  /**
   * Array containing the stand/end position for the highlight sequence
   */
  highlight: PropTypes.arrayOf(PropTypes.number).isRequired,
  /**
   * The string to set the color of highlighting
   */
  highlightClass: PropTypes.oneOf(["null", "red", "green", "blue"]),
  /**
   * The delay after which sliderChange event should be fired
   */
  onChangeTimeout: PropTypes.number,
  /**
   * Callback function to be executed when slider value changed
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Boolean value specifying if slide is enable/disable
   */
  disable: PropTypes.bool,
  /**
   * Label for the slider
   */
  label: PropTypes.string,
};

OverlaySlider.defaultProps = {
  highlight: [0, 1],
  sequence: "sequence",
  highlightClass: "null",
  onChangeTimeout: 400,
  disable: false,
  label: "",
};

/**
 * CharacterTag
 *  - Component that display a single character of sequence in  OverlaySlider
 */
function CharacterTag(props) {
  const elementProps = (() => {
    const { dataId, ...rest } = props;
    return {
      [DATA_ID]: dataId,
      ...rest,
    };
  })();
  if (props.character === SPACE_BAR) {
    return <span {...elementProps}>&nbsp;</span>;
  } else {
    return <span {...elementProps}>{props.character}</span>;
  }
}

CharacterTag.porpTypes = {
  /**
   * className to be set for the Character
   */
  className: PropTypes.string,
  /**
   * boolean value specifying if the character is draggable
   */
  draggable: PropTypes.bool,
  /**
   * callback function to execute when drag the character
   */
  onDragStart: PropTypes.func,
  /**
   * callback function to execute when draggedover the character
   */
  onDragOver: PropTypes.func,
  /**
   * The character to be displayed in the tag.
   */
  character: PropTypes.string,
  /**
   * The data-id value to be set on the character tag
   */
  dataId: PropTypes.number,
  /**
   * TabIndex to set on the element
   */
  tabIndex: PropTypes.number,
};
